import React from 'react';

import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import AstroLoader from './AstroLoader';
import useOverlayLifecycle from './useOverlayLifecycle';
import usePageLoader from './usePageLoader';

// O estilo do overlay é FONTE ÚNICA em `Astrolink/website/css/pageloader.css`
// (classes `.pageloader-overlay` / `--in` / `--leaving`), linkado no `_heads.php` do legado —
// o mesmo CSS estiliza o overlay crítico de fade-out da chegada. Aqui só orquestramos o estado.

const buttonFadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(6px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const TakingLongMessage = styled.p`
    margin: 0;
    max-width: 360px;
    padding: 24px;
    border-radius: 4px;
    text-align: center;
    color: #5a5a5a;
    background: white;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.55;
    font-weight: 400;

    animation: ${buttonFadeIn} 600ms cubic-bezier(0.2, 0.8, 0.2, 1) both;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const RecoveryLink = styled.a`
    color: #844ab0;
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
    cursor: pointer;
    transition: color 200ms ease;

    &:hover {
        color: #6d3994;
    }

    &:focus-visible {
        outline: 2px solid rgba(132, 74, 176, 0.4);
        outline-offset: 2px;
        border-radius: 2px;
    }
`;

/** Overlay de navegação full-page: fade-in na origem, cobre o load e faz fade-out ao ficar pronto. */
const PageLoaderApp = ({ intl }: InjectedIntlProps) => {
    const { isLoading, showReloadButton } = usePageLoader();
    const { isMounted, isLeaving, animateIn, onTransitionEnd } = useOverlayLifecycle(isLoading);

    if (!isMounted) return null;

    const loadingLabel = intl.formatMessage({ id: 'common.pageLoaderLoading' });

    const handleTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget || event.propertyName !== 'opacity') return;
        onTransitionEnd();
    };

    const overlayClassName = [
        'pageloader-overlay',
        animateIn && 'pageloader-overlay--in',
        isLeaving && 'pageloader-overlay--leaving'
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            className={overlayClassName}
            role="status"
            aria-live="polite"
            aria-label={loadingLabel}
            onTransitionEnd={handleTransitionEnd}
        >
            <AstroLoader />
            {showReloadButton && !isLeaving && (
                <TakingLongMessage>
                    <FormattedMessage
                        id="common.pageLoaderTakingLong"
                        values={{
                            link: (
                                <RecoveryLink href={`/${intl.formatMessage({ id: 'common.routes.personalArea' })}`}>
                                    <FormattedMessage id="common.pageLoaderGoBack" />
                                </RecoveryLink>
                            )
                        }}
                    />
                </TakingLongMessage>
            )}
        </div>
    );
};

export default injectIntl(PageLoaderApp);
